/**
 * Índice e ranqueamento da busca (§5.8.1, §5.8.3, §5.8.4), funções puras sobre os estáticos (`Geography` e
 * `CandidateList`): a busca não depende do feed e funciona antes das 17h, sem rede e em replay.
 *
 * Fora do índice nesta entrega (`docs/DECISOES.md` §29): países e cidades do exterior (os cartões deles são da Fase 8;
 * escolher um levaria a uma coluna vazia), zonas (P2, `features.zones`) e deputados (P2, `features.archive_1t`). O
 * "Exterior" entra, como já entra pelo marcador do mapa.
 *
 * Neutralidade: entre candidatos com a mesma ordem, o de menor número vem primeiro (regra fixa, AC-5.8-13); as
 * sugestões nunca têm candidato.
 */
import type { Candidate, CandidateList, Geography } from '../../contracts/static';
import { t } from '../../copy/strings';
import { fmtVoters } from '../../format/numbers';
import { placeName, ufOf } from '../../format/places';
import type { CandidateNumber, UfCode } from '../../model/types';
import { partyOf } from '../../theme/parties';
import { normalizeSearch, searchKeys } from './normalize';

export type SearchKind = 'uf' | 'municipality' | 'abroad' | 'candidate';

export type SearchTarget =
  | { kind: 'uf'; uf: UfCode }
  | { kind: 'municipality'; uf: UfCode; ibge: string }
  | { kind: 'abroad' }
  | { kind: 'candidate'; office: 'president'; number: CandidateNumber }
  | { kind: 'candidate'; office: 'governor'; uf: UfCode; number: CandidateNumber };

export type SearchBadge =
  | { type: 'tile'; text: string }
  | { type: 'globe' }
  | { type: 'portrait'; candidate: Candidate };

export interface SearchItem {
  id: string;
  kind: SearchKind;
  label: string;
  sub: string;
  /** Chaves normalizadas (§5.8.2). */
  keys: string[];
  /** Apelidos normalizados (casamento exato). */
  aliases: string[];
  weight: number;
  uf: UfCode | null;
  badge: SearchBadge;
  target: SearchTarget;
  /** Só municípios: ordena as sugestões. */
  population?: number;
  /** Só candidatos: desempate fixo por número (13 antes de 22). */
  number?: number;
}

export interface SearchIndex {
  items: SearchItem[];
  /** Municípios por população decrescente (sugestões, §5.8.4). */
  byPopulation: SearchItem[];
}

export interface SearchIndexFlags {
  abroad: boolean;
  governors: boolean;
}

/** Apelidos de capitais além da sigla da UF (§5.8.1), pelo IBGE. */
const CAPITAL_NICKNAMES: Readonly<Record<string, string>> = {
  '3106200': 'bh',
  '3550308': 'sampa',
  '5300108': 'bsb',
  '4314902': 'poa',
  '4205407': 'floripa',
};

const ABROAD_ALIASES = ['zz', 'fora do brasil', 'fora do pais', 'internacional', 'mundo'];

const sigla = (c: Pick<Candidate, 'party_code'>): string => partyOf(c).display || c.party_code;

const candidateKeys = (c: Candidate): string[] => [
  ...new Set([...searchKeys(c.display_name), ...searchKeys(c.ballot_name)]),
];

/** Monta o índice uma vez (§5.8.1). */
export function buildSearchIndex(
  geography: Geography,
  candidates: CandidateList | null | undefined,
  flags: SearchIndexFlags,
): SearchIndex {
  const items: SearchItem[] = [];
  for (const p of geography.places) {
    if (p.code === 'ZZ') continue;
    const uf = p.code as UfCode;
    const label = placeName(uf);
    items.push({
      id: `uf:${uf}`,
      kind: 'uf',
      label,
      sub: t(uf === 'DF' ? 'search.sub.df' : 'search.sub.uf'),
      keys: searchKeys(label),
      aliases: [uf.toLowerCase()],
      weight: 3,
      uf,
      badge: { type: 'tile', text: uf },
      target: { kind: 'uf', uf },
    });
  }
  const municipalities: SearchItem[] = [];
  for (const m of geography.municipalities) {
    const aliases = m.capital ? [m.uf.toLowerCase()] : [];
    const nick = CAPITAL_NICKNAMES[m.ibge];
    if (nick) aliases.push(nick);
    const item: SearchItem = {
      id: `mun:${m.ibge}`,
      kind: 'municipality',
      label: m.name,
      sub: t('search.sub.municipality', { E: fmtVoters(m.electorate_ref) }),
      keys: searchKeys(m.name),
      aliases,
      weight: 1 + Math.log10(Math.max(10, m.population)) / 10,
      uf: m.uf,
      badge: { type: 'tile', text: m.uf },
      target: { kind: 'municipality', uf: m.uf, ibge: m.ibge },
      population: m.population,
    };
    items.push(item);
    municipalities.push(item);
  }
  if (flags.abroad && geography.abroad_cities.length > 0) {
    const label = placeName('ZZ');
    items.push({
      id: 'abroad',
      kind: 'abroad',
      label,
      sub: t('search.sub.abroad', { N: geography.abroad_cities.length, M: geography.countries.length }),
      keys: searchKeys(label),
      aliases: ABROAD_ALIASES,
      weight: 2.8,
      uf: null,
      badge: { type: 'globe' },
      target: { kind: 'abroad' },
    });
  }
  if (candidates) {
    for (const c of candidates.president) {
      items.push({
        id: `cand:president:${c.number}`,
        kind: 'candidate',
        label: c.display_name,
        sub: t('search.sub.president', { SIGLA: sigla(c), NUM: c.number }),
        keys: candidateKeys(c),
        aliases: [],
        weight: 2.6,
        uf: null,
        badge: { type: 'portrait', candidate: c },
        target: { kind: 'candidate', office: 'president', number: c.number },
        number: Number(c.number),
      });
    }
    if (flags.governors) {
      for (const [uf, pair] of Object.entries(candidates.governor) as Array<[UfCode, [Candidate, Candidate]]>) {
        for (const c of pair) {
          items.push({
            id: `cand:governor:${uf}:${c.number}`,
            kind: 'candidate',
            label: c.display_name,
            sub: t('search.sub.governor', { uf_de: ufOf(uf), SIGLA: sigla(c), NUM: c.number }),
            keys: candidateKeys(c),
            aliases: [],
            weight: 2,
            uf,
            badge: { type: 'portrait', candidate: c },
            target: { kind: 'candidate', office: 'governor', uf, number: c.number },
            number: Number(c.number),
          });
        }
      }
    }
  }
  const byPopulation = [...municipalities].sort((a, b) => (b.population ?? 0) - (a.population ?? 0));
  return { items, byPopulation };
}

/** No máximo 8 resultados (§5.8.3 passo 6). */
export const MAX_RESULTS = 8;
/** 6 sugestões (§5.8.4). */
export const MAX_SUGGESTIONS = 6;

/** Ordem do casamento de texto (§5.8.3 passo 3); null sem casamento. */
function matchOrder(item: SearchItem, q: string): number | null {
  if (item.aliases.includes(q)) return item.kind === 'uf' ? -2 : -1;
  let best: number | null = null;
  for (const k of item.keys) {
    const o = k === q ? 0 : k.startsWith(q) ? 1 : k.includes(` ${q}`) ? 2 : k.includes(q) ? 3 : null;
    if (o !== null && (best === null || o < best)) best = o;
  }
  return best;
}

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/** Os resultados de uma consulta de texto (§5.8.3); vazia → [] (quem chama mostra as sugestões). */
export function searchQuery(index: SearchIndex, query: string, focusUf: UfCode | null): SearchItem[] {
  const q = normalizeSearch(query);
  if (q === '') return [];
  const hits: Array<{ item: SearchItem; order: number; weight: number }> = [];
  for (const item of index.items) {
    const order = matchOrder(item, q);
    if (order === null) continue;
    const bonus = item.kind === 'municipality' && focusUf !== null && item.uf === focusUf ? 0.5 : 0;
    hits.push({ item, order, weight: item.weight + bonus });
  }
  hits.sort(
    (a, b) =>
      a.order - b.order ||
      b.weight - a.weight ||
      (a.item.kind === 'candidate' && b.item.kind === 'candidate' ? (a.item.number ?? 0) - (b.item.number ?? 0) : 0) ||
      collator.compare(a.item.label, b.item.label),
  );
  return hits.slice(0, MAX_RESULTS).map((h) => h.item);
}

/**
 * Sugestões da consulta vazia (§5.8.4): as maiores cidades do país ou da UF em foco; nunca candidatos. No Exterior
 * (sem UF em foco), as do país: as cidades do exterior ficam fora do índice até a Fase 8 (`docs/DECISOES.md` §29).
 */
export function searchSuggestions(index: SearchIndex, focusUf: UfCode | null): SearchItem[] {
  const list = focusUf === null ? index.byPopulation : index.byPopulation.filter((m) => m.uf === focusUf);
  return list.slice(0, MAX_SUGGESTIONS);
}
